/* globals window */
import $ from 'jquery';
import _, { capitalize } from 'lodash';
import moment from 'moment';
import WebUrls from './constants/web-urls';
import { TIME_NOT_SELECTED } from './constants/constants';
import { Solutions, Roles, PagePaths } from './constants/enums';
import RoleAccessGroup from './constants/role-access-groups';
import { COLORS } from '../common/styles/variables';

const { getUserRole, getUserPermissions } = require('./permissions').default;

const RATINGS = {
  POSITIVE: 'positive',
  NEUTRAL: 'neutral',
  NEGATIVE: 'negative',
  NOT_APPLICABLE: 'not-applicable',
};

/* eslint-disable no-useless-escape */
/* eslint-disable import/no-unresolved */
/* eslint-disable class-methods-use-this */
// ^^ because this is part of a build process so relative dir will change

export default class BpUtils {
  static loginPage = WebUrls.LOGIN_PAGE;
  static homePage = WebUrls.HOME_PAGE;
  static resetPasswordPage = WebUrls.RESET_PASSWORD;
  static forgotPasswordPage = WebUrls.FORGOT_PASSWORD;
  static signupPage = WebUrls.Signup;
  static env = require('../../build-static/env.json');
  static publicUrls = [
    BpUtils.loginPage,
    BpUtils.resetPasswordPage,
    BpUtils.forgotPasswordPage,
    BpUtils.signupPage,
  ];

  static PagePaths = {};

  static store = require('store');
  static apiProp;
  static api;

  static get api() {
    if (!BpUtils.apiProp) {
      BpUtils.apiProp = require('./api').default;
    }
    return BpUtils.apiProp;
  }

  static appInit() {
    BpUtils.verifyAccessToken();
    BpUtils.setUserProfileInfo();

    BpUtils.updateAppVisibility();
    BpUtils.updateDocumentTitle();
    BpUtils.searchAndReplaceAppDisplayName();
  }

  static getAppName() {
    return 'business_pages';
  }

  static getAppDisplayName() {
    return 'GasBuddy Business Pages';
  }

  static getCurrentAppOrganizations(orgs) {
    return orgs.filter(o => (o.is_business_pages));
  }

  static updateAppVisibility() {
    const { app } = window.PreloadedState;
    // .hidden-{{app_name}} elements will be hidden with CSS,
    // but to be safe, this method will flat-out remove those elements
    // from the DOM
    $(`.hidden-${app.name}`).remove();
  }

  static updateDocumentTitle() {
    // while we're doing this on every page load, let's also update the page title
    document.title = `${document.title} | ${BpUtils.getAppDisplayName()}`;
  }

  static clearUserSession() {
    const keyToBePersisted = [];

    BpUtils.store.each((value, key) => {
      if (key.indexOf('/client/') > -1 || key.indexOf('_permanent_store') > -1) {
        keyToBePersisted.push({ key, value });
      }
    });

    BpUtils.store.clearAll();

    // restore keys to be persisted even after logout
    keyToBePersisted.forEach((data) => {
      BpUtils.store.set(data.key, data.value);
    });
  }

  static logOut(redirectToLogin = true, callback) {
    BpUtils.api.delete({
      path: '/session',
      data: {},
      complete: () => {
        BpUtils.clearUserSession();
        if (redirectToLogin) {
          BpUtils.redirectAndKeepCurrentParams(BpUtils.loginPage, false);
        }
        BpUtils.invokeCallback(callback);
      },
    });
  }

  static processLoginResponse(loginResponse, brandNewSession = true) {
    const redirect = BpUtils.getRedirectPath() || BpUtils.getHomePageURL();
    if (brandNewSession) {
      BpUtils.saveUserSession(loginResponse);
    }
    if (loginResponse.profile) {
      BpUtils.saveUserProfile(loginResponse.profile);
      if (loginResponse.profile.organization) {
        BpUtils.saveUserOrganization(loginResponse.profile.organization);
      }
    }

    BpUtils.store.set('currentPageURL', redirect);
    window.location.href = redirect;
  }

  static saveUserSession(loginResponse) {
    // This will not associate the session with a user or organization
    BpUtils.store.set('auth_context_id', loginResponse.auth_context_id);
    BpUtils.store.set('refresh_token', loginResponse.refresh_token);
    BpUtils.store.set('token_type', loginResponse.token_type);
    BpUtils.store.set('access_token', loginResponse.access_token);
  }

  static saveUserProfile(userProfile) {
    const hasUserName = (userProfile.first_name || userProfile.last_name);
    BpUtils.store.set('username', (hasUserName ? `${userProfile.first_name} ${userProfile.last_name}` : userProfile.email));
    BpUtils.store.set('email', userProfile.email);
    BpUtils.store.set('title', userProfile.title);
    BpUtils.store.set('role', userProfile.roles[0]);
    BpUtils.store.set('department', userProfile.department);
    BpUtils.store.set('permissions', userProfile.permissions);
  }

  static saveUserOrganization(organization) {
    if (!organization) {
      BpUtils.logOut();
    }
    BpUtils.store.set('current_org_id', organization.organization_id);
    BpUtils.store.set('org_name', organization.display_name);
    BpUtils.store.set('org_logo_url', organization.logo_url);
    BpUtils.store.set('org_contract_status', organization.contract_status);

    const uniqOrgSolutionOptionNames = _(organization.solutions)
      .map(s => s.options)
      .flatten()
      .compact()
      .uniqBy(o => o.name)
      .map(o => o.name)
      .value();

    const orgSolutionNames = organization.solutions.map(s => s.name);

    BpUtils.store.set('org_solutions', orgSolutionNames);
    BpUtils.store.set('org_solution_options', uniqOrgSolutionOptionNames);
  }

  static setDefaultOrgAsCurrentOrg(retry, errorCallback) {
    BpUtils.api.get({
      path: '/organizations',
      requestId: 'loadAvailableOrganizations',
      error(err) {
        if (retry) {
          BpUtils.setDefaultOrgAsCurrentOrg(false, errorCallback);
        }
        BpUtils.invokeCallback(errorCallback, err);
      },
      success({ emergency_status: emergencyStatus, organizations: orgs }) {
        const currentAppOrgs = BpUtils.getCurrentAppOrganizations(orgs);
        if (currentAppOrgs.length > 0) {
          // Set "first app-specific org" as selected-org
          // (later user will be able to set default-org in user-profile)
          const currentOrgId = currentAppOrgs[0].organization_id;
          BpUtils.setOrganizationForSession(currentOrgId, errorCallback);
          BpUtils.setEmergencyStatus(emergencyStatus);
        }
      },
    });
  }

  static setOrganizationForSession(orgId, errorCallback) {
    BpUtils.api.post({
      path: '/session/organization',
      requestId: 'setOrganizationForSession',
      data: {
        organization_id: orgId,
      },
      error(err) {
        BpUtils.invokeCallback(errorCallback, err);
      },
      success(res) {
        BpUtils.processLoginResponse(res, false);
      },
    });
  }

  static getLoginURL() {
    return BpUtils.loginPage;
  }

  static getHomePageURL() {
    return BpUtils.homePage;
  }

  static redirectAndKeepCurrentParams(newPageURL, setCurrentPageAsRedirectPath) {
    const { port, protocol, hostname } = window.location;
    const hostName = (port === '' ? hostname : `${hostname}:${port}`);
    const windowLocation = window.location.href;
    let redirectUrl = '';
    if (windowLocation.indexOf('redirectPath=') !== -1) {
      redirectUrl = BpUtils.getRedirectPath();
    } else if (setCurrentPageAsRedirectPath) {
      redirectUrl = windowLocation.replace(`${protocol}//`, '').replace(hostName, '');
    } else {
      redirectUrl = '';
    }
    window.location.href = redirectUrl === '' ? newPageURL : `${newPageURL}?redirectPath=${redirectUrl}`;
  }

  static verifyAccessToken(callback) {
    if ($.inArray(window.location.pathname, BpUtils.publicUrls) < 0) {
      // on client pages, if accesstoken does not exist, attempt session refresh
      const accessToken = BpUtils.store.get('access_token');
      if (!accessToken || accessToken === '' || getUserPermissions() === null) {
        // Try regenerating session once with available cookies
        BpUtils.api.refreshSession(callback);
      } else {
        // Session ongoing
        BpUtils.invokeCallback(callback);
      }
    } else {
      // No need to check session, on a public page
      BpUtils.invokeCallback(callback);
    }
  }

  static getOrgName() {
    return BpUtils.store.get('org_name');
  }

  static getOrgSolutions() {
    return BpUtils.store.get('org_solutions') || [];
  }

  static getOrgSolutionOptions() {
    return BpUtils.store.get('org_solution_options') || [];
  }

  static getOrgContractStatus() {
    return BpUtils.store.get('org_contract_status') || '';
  }

  static getOrgLogoUrl() {
    return BpUtils.store.get('org_logo_url');
  }

  static getAccessToken() {
    return BpUtils.store.get('access_token');
  }

  static getLoggedInUserEmail() {
    return BpUtils.store.get('email');
  }

  static getLoggedInUserName() {
    return BpUtils.store.get('username');
  }

  static getLoggedInUserTitle() {
    return BpUtils.store.get('title');
  }

  static getLoggedInUserDepartment() {
    return BpUtils.store.get('department');
  }

  static getRedirectPath() {
    const { pathname, hostname, port, protocol } = window.location;
    if (pathname.indexOf(BpUtils.loginPage) >= 0) {
      const pageUrl = BpUtils.loginPage;
      let replacementPlaceholder = '';
      if (port === '') {
        replacementPlaceholder = `${protocol}//${hostname}${pageUrl}?redirectPath=`;
      } else {
        replacementPlaceholder = `${protocol}//${hostname}:${port}${pageUrl}?redirectPath=`;
      }
      const redirectPath = window.location.href.replace(replacementPlaceholder, '');

      const loginPageUrl = BpUtils.getLoginURL();
      if (redirectPath.indexOf(loginPageUrl) === -1) {
        return redirectPath;
      }
      return '';
    }
    return '';
  }

  static invokeCallback(callback, param1, param2, param3) {
    if (callback && typeof callback === 'function') {
      callback(param1, param2, param3);
    }
  }

  static getSupportEmail() {
    return 'pages@gasbuddy.com';
  }

  static isEmail(str) {
    const emailReg = /^([A-Za-z0-9_\-\.\+])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,6})$/;
    return emailReg.test(str);
  }

  static searchAndReplaceAppDisplayName() {
    [...document.getElementsByClassName('app-display-name')].forEach((element) => {
      element.innerHTML = BpUtils.getAppDisplayName();
    });
  }

  static getQueryParameters() {
    let queryParameters = null;
    const decodedQueryString = decodeURIComponent(window.location.search.trim().replace('?', ''));
    if (decodedQueryString) {
      queryParameters = {};
      const params = decodedQueryString.split('&');
      params.forEach((q) => {
        const key = q.split('=')[0];
        const value = q.split('=')[1];
        queryParameters[key] = value;
      });
    }
    return queryParameters;
  }

  static setQueryParameter(key, value) {
    const { location, history } = window;
    let newLocation;
    const currentLocation = location.href;
    const queryString = location.search;

    const encodedValue = encodeURIComponent(value);

    if (!queryString) {
      // append first query string parameter
      newLocation = `${currentLocation}?${key}=${encodedValue}`;
    } else if (currentLocation.indexOf(key) >= 0) {
      // replace query parameter value
      const pattern = `(${key})\=[^\&]*`;
      const replacementRegex = new RegExp(pattern, 'i');
      newLocation = currentLocation.replace(replacementRegex, `${key}=${encodedValue}`);
    } else {
      // append query string parameter
      newLocation = `${currentLocation}&${key}=${encodedValue}`;
    }

    // set query parameter in url but do not cause page reload
    history.replaceState({ path: newLocation }, null, newLocation);
  }

  static removeQueryParameter(key) {
    const { location, history } = window;
    const currentLocation = location.href;

    const pattern = `(${key})\=[^\&]*`;
    const replacementRegex = new RegExp(pattern, 'i');
    const newLocation = currentLocation.replace(replacementRegex, '');
    // set query parameter in url but do not cause page reload
    history.replaceState({ path: newLocation }, null, newLocation);
  }

  static getGATrackingId() {
    return BpUtils.env?.ga_tracking_id;
  }

  static isFeatureEnabled(featureName) {
    const { features } = BpUtils.env;
    features.emergency_features = BpUtils.store.get('emergency_features');
    // TODO: Use localstore value for emergency_status
    return features[featureName] === true;
  }

  static getEnvName() {
    return BpUtils.env?.env;
  }

  static testPasswordRules(value) {
    return /[A-Z]/.test(value) // has an uppercase letter
      && /\d/.test(value); // has a digit
  }

  static getParameterByName(name, url) {
    let urlAddress = url;
    if (!urlAddress) {
      urlAddress = window.location.href;
    }
    const formattedName = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${formattedName}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(urlAddress);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  static isUrl(str) {
    const reg = /^((http[s]*:\/\/)?(www\.)?)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
    return reg.test(str);
  }

  static getGoogleMapsAPIKey() {
    return 'AIzaSyDdBznIsL44xA_uNTSDaEJzWOnMV8sUB5Y';
  }

  static formatDatePeristTimeZone(dateString, format) {
    const timezone = moment.parseZone(dateString);
    return timezone.format(format);
  }

  static convertTimeTo12HoursFormat(hours24) {
    if (hours24 === TIME_NOT_SELECTED) {
      return TIME_NOT_SELECTED;
    }
    const splitted = hours24.split(':');
    return moment({ hour: splitted[0], minute: splitted[1] }).format('h:mma');
  }

  static getPriorPeriod(start, end) {
    const differenceInDays = end.diff(start, 'days');

    // End on the day before the start
    const priorEndDate = start.clone().subtract(1, 'days');

    // Go back X days
    const priorStartDate = priorEndDate.clone().subtract(differenceInDays, 'days');

    return {
      start: priorStartDate,
      end: priorEndDate,
    };
  }

  static logError(err) {
    // eslint-disable-next-line no-console
    console.log('BP_ERROR:', err);
  }

  static sortByFuelTypes(fuelTypesArr) {
    const expectedOrder = ['regular_gas', 'midgrade_gas', 'premium_gas', 'diesel', 'e85', 'e15'];

    return fuelTypesArr.sort((a, b) => (expectedOrder.indexOf(a.id) < expectedOrder.indexOf(b.id) ? -1 : 1));
  }

  static getNavigationTree() {
    try {
      const isEmergency = BpUtils.isFeatureEnabled('emergency_features');
      const currentUserRole = getUserRole();
      const navigationItems = [{
        name: 'Emergency Update',
        key: 'emergency',
        cssClassPrefix: 'feature-toggle-emergency_features feature-disabled emergency-updates-link',
        secondaryNavCssClass: 'emergency-menu',
        iconName: '',
        requiredSolutions: null,
        requiredRoles: RoleAccessGroup.Any,
        children: [{
          name: 'Station Status Updater',
          link: '/client/emergency/station-updates.html',
          requiredSolutions: null,
          requiredRoles: RoleAccessGroup.Any,
        }, {
          name: 'Outage Tracker',
          link: '/client/emergency/outage-tracker.html',
          requiredSolutions: null,
          requiredRoles: RoleAccessGroup.Any,
        }],
      },
      {
        name: 'Overview',
        key: 'overview',
        cssClassPrefix: 'overview-link',
        iconName: 'home',
        requiredSolutions: null,
        requiredRoles: null,
        children: [{
          name: 'Dashboard Summary',
          link: '/client/home.html',
          requiredSolutions: null,
          requiredRoles: null,
        }],
      }, {
        name: 'My Listings',
        key: 'my-listings',
        cssClassPrefix: 'my-listings-link',
        iconName: 'listings',
        requiredSolutions: null,
        requiredRoles: RoleAccessGroup.Any,
        children: [{
          name: 'Station Lists',
          link: '/client/station-profiles/station-list.html',
          requiredSolutions: null,
          requiredRoles: RoleAccessGroup.Any,
        }, {
          name: 'Station Profile',
          link: PagePaths.StationProfile,
          requiredSolutions: null,
          requiredRoles: RoleAccessGroup.Any,
          // pageToggle: {
          //   buttonText: 'Original Station Profile',
          //   headerText: 'Can’t find something you’re looking for?',
          //   // eslint-disable-next-line max-len
          //   secondaryText: 'Click the Original Station Profile button to go back.',
          // },
        // }, {
        //   name: 'Suggested Edits',
        //   link: '/client/my-listings/suggested-edits',
        //   requiredSolutions: [Solutions.Listings],
        //   requiredRoles: RoleAccessGroup.Editor,
        }, {
          name: 'Upload Fuel Prices',
          link: '/client/station-profiles/fuel-prices.html',
          requiredSolutions: [Solutions.Listings],
          requiredRoles: RoleAccessGroup.Editor,
        }],
      }, {
        name: 'Reputation Management',
        key: 'reputation-management',
        cssClassPrefix: 'reputation-management-link',
        iconName: 'reviews',
        requiredSolutions: [Solutions.ReputationManagement],
        requiredRoles: RoleAccessGroup.Observer,
        children: [{
          name: 'Station Reviews',
          link: '/client/station-profiles/reviews',
          requiredSolutions: [Solutions.ReputationManagement],
          requiredRoles: RoleAccessGroup.Observer,
        }, {
          name: 'Station Ratings',
          link: '/client/engagement-analytics/station-ratings',
          requiredSolutions: [Solutions.ReputationManagement],
          requiredRoles: RoleAccessGroup.Observer,
        }, {
          name: 'Review Reply Templates',
          link: '/client/station-profiles/review-reply-templates',
          requiredSolutions: [Solutions.ReputationManagement],
          requiredRoles: RoleAccessGroup.Administrator,
        }],
      }, {
        name: 'Insights',
        key: 'market-insight',
        cssClassPrefix: 'market-insight-link',
        iconName: 'insight',
        requiredSolutions: [Solutions.Insights],
        requiredRoles: RoleAccessGroup.Observer,
        children: [{
          name: 'Competitor Benchmarking',
          link: '/client/engagement-analytics/brand-benchmark.html',
          requiredSolutions: [Solutions.Insights],
          requiredRoles: RoleAccessGroup.Observer,
        }, {
          name: 'Brand Engagement',
          link: '/client/engagement-analytics/brand-engagement',
          requiredSolutions: [Solutions.Insights],
          requiredRoles: RoleAccessGroup.Observer,
        }],
      }, {
        name: 'Reports',
        key: 'alerts-and-reports',
        cssClassPrefix: 'alerts-link',
        iconName: 'alerts',
        requiredSolutions: [Solutions.ReputationManagement],
        requiredRoles: RoleAccessGroup.Editor,
        children: [{
          name: 'Report Management',
          link: '/client/alerts',
          requiredSolutions: [Solutions.ReputationManagement],
          requiredRoles: RoleAccessGroup.Editor,
        }],
      }, {
        name: 'Users & Permissions',
        key: 'users-and-permissions',
        cssClassPrefix: 'users-and-permissions-link',
        iconName: 'users_and_permissions',
        requiredSolutions: null,
        requiredRoles: RoleAccessGroup.Administrator,
        children: [{
          name: 'User Management',
          link: '/client/users/manage-users',
          requiredSolutions: null,
          requiredRoles: RoleAccessGroup.Administrator,
        }],
      }];

      if (!isEmergency) {
        navigationItems.splice(navigationItems.indexOf(navigationItems.find(ni => ni.key === 'emergency')), 1);
      }

      const matchSolution = (menu) => {
        if (!menu.requiredSolutions) {
          return true;
        }
        return menu.requiredSolutions.some(s => BpUtils.hasSolution(BpUtils.getOrgSolutions(), s));
      };

      const matchRole = (menu) => {
        if (!menu.requiredRoles) {
          return true;
        }
        if (currentUserRole === Roles.Fuel_Shortage) {
          return isEmergency && menu.requiredRoles.includes(currentUserRole);
        }
        return menu.requiredRoles.includes(currentUserRole);
      };

      const filteredNavItems = _(navigationItems)
        .filter(m => matchSolution(m) && matchRole(m))
        .map(m => ({ ...m, children: m.children.filter(c => matchSolution(c) && matchRole(c)) }))
        .value();

      let selectedPrimaryNavItem = null;
      let selectedSecondaryNavItem = null;
      const { pathname } = window.location;

      filteredNavItems.forEach((navItem) => {
        navItem.children.forEach((subNavItem) => {
          if (pathname.includes(subNavItem.link.replace('.html', ''))) {
            selectedPrimaryNavItem = navItem;
            selectedSecondaryNavItem = subNavItem;
          }
        });
      });

      return {
        navigationItems: filteredNavItems,
        selectedPrimaryNavItem,
        selectedSecondaryNavItem,
        hoveredPrimaryNavItem: selectedPrimaryNavItem,
      };
    } catch (err) {
      return {
        navigationItems: [],
        selectedPrimaryNavItem: null,
        selectedSecondaryNavItem: null,
        hoveredPrimaryNavItem: null,
      };
    }
  }

  static getStationBrand = (brandings) => {
    if (brandings?.cstore_brand_id) {
      return {
        brand_id: brandings.cstore_brand_id,
        brand_logo: brandings.cstore_brand_logo_url,
      };
    }
    if (brandings?.fuel_brand_id) {
      return {
        brand_id: brandings.fuel_brand_id,
        brand_logo: brandings.fuel_brand_logo_url,
      };
    }
    return {
      brand_id: 0,
      brand_logo: 'https://images.gasbuddy.io/b/0.png',
    };
  };

  static showCiNaWhenZero(value, cssClass = '') {
    // ^^^^ not in use at this time
    if (!value || value === 'N/A' || Number(value) === 0) {
      return `<div data-toggle="tooltip" title="The station(s) currently does not qualify for a score" class="${cssClass} unavailable">N/A</div>`;
    }
    return value;
  }

  static compare(actual, target, decimalPoints = 0, isCompetitiveIndex = false) {
    const actualNum = Number(actual);
    const targetNum = Number(target);
    // CI needs to be shown a N/A when either station or competitor has CI=0
    if (isCompetitiveIndex && (Number.isNaN(actualNum) || Number.isNaN(targetNum) || actual === 0 || target === 0)) {
      return BpUtils.showCiNaWhenZero(0);
    }
    let result = 0;
    if (actual === undefined || actual === null || target === undefined || target === null) {
      // When nothing to compare
      return undefined;
    }
    result = (actual - target).toFixed(decimalPoints);
    // this gets rid of any time there is a negative 0
    if (result === '-0') { result = '0'; }
    return Number(result);
  }

  static convertToTimeDuration(numberOfSeconds) {
    if ((!numberOfSeconds && numberOfSeconds !== 0) || Number.isNaN(numberOfSeconds)) {
      return NaN;
    }
    const duration = moment.duration(Number(numberOfSeconds), 'seconds');
    if (numberOfSeconds < 0) {
      return `${duration.minutes()}m ${-duration.seconds()}s`;
    }
    return `${duration.minutes()}m ${duration.seconds()}s`;
  }

  static shortenLargeNumber(integerValue) {
    if (!integerValue || Number.isNaN(Number(integerValue))) {
      return integerValue;
    }

    let isNegative = false;
    let result = integerValue;

    if (integerValue < 0) {
      result = -result;
      isNegative = true;
    }

    // shorten number only if greater than 99999
    if (result > 99999) {
      const units = ['k', 'M', 'B'];
      let decimal;
      for (let i = units.length - 1; i >= 0; i -= 1) {
        decimal = 1000 ** (i + 1);

        if (result <= -decimal || result >= decimal) {
          result = +(result / decimal).toFixed(2) + units[i];
        }
      }
    }
    return `${(isNegative ? '-' : '')}${result.toLocaleString()}`;
  }

  // takes in a hour (24h format) and convert to 12 hours and determines if its in the am or pm
  static convert24HourTo12Hour(hourValue) {
    let hour = hourValue;
    let meridiemPrefix = 'am';
    if (hour >= 12) {
      meridiemPrefix = 'pm';
    }
    hour = hour > 12 ? hour - 12 : hour;
    if (hour === 0) {
      hour = 12;
    }
    return `${hour}${meridiemPrefix}`;
  }

  static setEmergencyStatus(emergencyStatus) {
    BpUtils.store.set('emergency_features', emergencyStatus);
  }

  static getNumberOfDays(startDate, endDate) {
    // Need add 1 because e.g. for 01-Jan to 31-Jan, it returns 30, but we expect 31 days;
    return moment(endDate).diff(startDate, 'days') + 1;
  }

  static toPascalCase(str) {
    return _(str).split(' ').map(capitalize).join(' ')
      .valueOf();
  }

  static isSuperAdmin() {
    // @gasbuddy.io user should always be able to view all solutions (but based on user-role)
    return BpUtils.getLoggedInUserEmail().toLowerCase().endsWith('@gasbuddy.io');
  }

  static hasSolution(solutions, solution) {
    if (BpUtils.isSuperAdmin()) {
      return true;
    }
    return !!solutions.find(s => s === solution);
  }

  static ellipsedText(text, maxLength) {
    if (text.length > maxLength) {
      return `${text.substr(0, maxLength)}...`;
    }
    return text;
  }

  // This function is also replicated in BP-Serv in src/services/reports/station-profile-report-service.js
  // So that any changes can be copy-pasted easily
  static getAveragePeakDwellTimeValues(hourlyVisitsMap, dwellTimePropName) {
    const hours = Object.keys(hourlyVisitsMap);
    const visitingTimesArr = hours.map(hour => Object.assign({}, hourlyVisitsMap[hour], { hour }));
    const maxVisitCount = _.maxBy(visitingTimesArr, 'visit_count').visit_count;
    let maxValue = 0;
    let maxIndex = 0;

    // loop through array of objects with visits and avg_dwell time in it
    // add together the hour's value + hour-1 value + hour+1 value and dividing by 3
    // to find the hour that has the peak number of visits
    // dont itterate through first or last because we need the hour to have + or - 1 value
    for (let i = 1; i < visitingTimesArr.length - 1; i += 1) {
      const indexValue = visitingTimesArr[i].visit_count;
      if (indexValue / maxVisitCount > 0.6) {
        const value = Math.round((visitingTimesArr[i - 1].visit_count
          + visitingTimesArr[i].visit_count + visitingTimesArr[i + 1].visit_count) / 3);
        if (value > maxValue) {
          maxValue = value;
          maxIndex = i;
        }
      }
    }
    const middleHourKey = Number(visitingTimesArr[maxIndex].hour);
    const firstHourKey = middleHourKey - 1;
    const thirdHourKey = middleHourKey + 1;

    const firstHour = firstHourKey.toString();
    const endHour = (thirdHourKey + 1).toString();
    const avgPeakDwellTime = Math.round((
      ((hourlyVisitsMap[firstHourKey] || {})[dwellTimePropName] || 0)
      + ((hourlyVisitsMap[middleHourKey] || {})[dwellTimePropName] || 0)
      + ((hourlyVisitsMap[thirdHourKey] || {})[dwellTimePropName] || 0)) / 3);

    const avgPeakDwellMinutes = Math.floor(avgPeakDwellTime / 60);
    const avgPeakDwellSeconds = avgPeakDwellTime % 60;

    return { avgPeakDwellMinutes, avgPeakDwellSeconds, firstHour, endHour };
  }

  static formatPeakHoursRange = (startHour, endHour) => {
    const startHourString = BpUtils.convert24HourTo12Hour(startHour.toString());
    const endHourString = BpUtils.convert24HourTo12Hour(endHour.toString());

    return `${startHourString} to ${endHourString}`;
  }

  // Returns New Or Legacy Page Path based on user's preference
  // As of now, only applicable for Station Profile Page
  static getPagePath(requestedPath) {
    const actualPath = BpUtils.store.get(requestedPath);
    if (actualPath) {
      return actualPath;
    }
    return requestedPath;
  }

  static getMostRecentStationListId() {
    return BpUtils.store.get('mostRecentStationListId') || 0;
  }

  static setMostRecentStationListId(val) {
    return BpUtils.store.set('mostRecentStationListId', val);
  }

  // 1-2.9 is red   3.0-3.9 is blue  4.0+ is green
  static convertStarRatingToCategoryRating = (starRating) => {
    if (starRating >= 1.0 && starRating < 3.0) {
      return RATINGS.NEGATIVE;
    } if (starRating >= 3.0 && starRating < 4.0) {
      return RATINGS.NEUTRAL;
    } if (starRating >= 4.0) {
      return RATINGS.POSITIVE;
    }
    return RATINGS.NOT_APPLICABLE;
  }

  // 1-40 is red   41-70 is blue  >70 is green
  static convertCompetitiveIndextoCategoryRating = (competitiveIndex) => {
    if (competitiveIndex <= 40 && competitiveIndex > 0) {
      return RATINGS.NEGATIVE;
    } if (competitiveIndex >= 41 && competitiveIndex <= 70) {
      return RATINGS.NEUTRAL;
    } if (competitiveIndex >= 71) {
      return RATINGS.POSITIVE;
    }
    return RATINGS.NOT_APPLICABLE;
  }

  static convertCategoryRatingToHexColor = (categoryRating) => {
    switch (categoryRating) {
      case RATINGS.POSITIVE:
        return COLORS.positive;
      case RATINGS.NEGATIVE:
        return COLORS.negative;
      case RATINGS.NEUTRAL:
        return COLORS.neutral;
      case RATINGS.NOT_APPLICABLE:
      default:
        return COLORS.unavailable;
    }
  }

  static convertStarRatingToHexColor = starRating => BpUtils.convertCategoryRatingToHexColor(BpUtils.convertStarRatingToCategoryRating(starRating))

  /* eslint-disable-next-line max-len */
  static convertCompetitiveIndexToHexColor = competitiveIndex => BpUtils.convertCategoryRatingToHexColor(BpUtils.convertCompetitiveIndextoCategoryRating(competitiveIndex))
}
